<script lang="ts">
	import { Responsive } from "responsive"
	import type { Snippet } from "svelte"
	import { fade } from "svelte/transition"

	const FADE_SETTINGS = { duration: 200 }

	interface Props {
		title: string
		heading?: Snippet
		description: string

		imagePreviewSrc: string
		videoPreviewSrc?: string

		cornerText?: string
		children?: Snippet
		bottomRow?: Snippet
	}

	let {
		title,
		description,
		imagePreviewSrc,
		videoPreviewSrc,
		cornerText,
		heading,
		children,
		bottomRow,
	}: Props = $props()

	let showVideo = $state(false)
	let hasVideo = $derived(!!videoPreviewSrc)

	function onHover() {
		showVideo = true
	}

	function onLeave() {
		showVideo = false
	}
</script>

<div
	class="showcase-tile"
	class:mobile={Responsive.isMobile}
	role="button"
	tabindex="0"
	title={description}
	onmouseover={onHover}
	onmouseleave={onLeave}
	onfocus={onHover}
	onblur={onLeave}
>
	<div class="theme-preview">
		{#if showVideo && hasVideo}
			<video autoplay muted loop in:fade={FADE_SETTINGS} out:fade={FADE_SETTINGS}>
				<source src={videoPreviewSrc} type="video/mp4" />
			</video>
		{:else}
			<img src={imagePreviewSrc} alt={title} in:fade={FADE_SETTINGS} out:fade={FADE_SETTINGS} />
		{/if}
		{#if hasVideo && !cornerText}
			<span class="interactive-info"> {Responsive.isMobile ? "Tap" : "Hover"} to preview</span>
		{:else if cornerText}
			<span class="interactive-info"> {cornerText} </span>
		{/if}
		{#if children}
			{@render children()}
		{/if}
	</div>
	<div class="theme-info">
		{#if heading}
			{@render heading()}
		{:else}
			<h3 class="title">{title}</h3>
		{/if}
		<p class="description">{description}</p>
		<div class="bottom-row">
			{#if bottomRow}
				{@render bottomRow()}
			{/if}
		</div>
	</div>
</div>

<style>
	.showcase-tile {
		position: relative;
		display: flex;
		flex-direction: column;
		background-color: var(--color-bg-dark);
		border-radius: var(--br);
		overflow: hidden;
		box-shadow: var(--shadow-medium);
		/* border: 1px solid var(--color-border); */
		width: calc(33.3cqw - 0.65rem);
	}

	.showcase-tile.mobile {
		width: 100%;
	}

	.theme-preview {
		position: relative;
		aspect-ratio: 16/9;
		height: fit-content;
		z-index: 1;
	}

	.interactive-info {
		position: absolute;
		z-index: 2;
		top: 0;
		right: 0;
		font-size: var(--h3);
		color: var(--color-text-light);
		padding: 0.5rem;
		line-height: 1em;
		border-bottom-left-radius: var(--br);
		background-color: var(--color-bg);
	}

	.theme-info {
		padding: clamp(1vw, 0.5rem, 1rem);
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		background-color: var(--color-bg-darker);
		height: 100%;
		z-index: 2;
	}

	.title {
		font-size: var(--h1);
		margin: 0;
	}

	.description {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
		line-height: 1.2;
		min-height: 2.5em;
		margin-bottom: 0;
		color: var(--color-text);
		flex-grow: 1;
	}

	video,
	img {
		position: absolute;
		object-fit: cover;
		width: 100%;
		height: 100%;
		pointer-events: none;
	}

	.mobile .title {
		font-size: var(--h2);
	}

	.mobile .description {
		font-size: var(--h3);
	}
</style>
